import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMessage } from "../contexts/MessageContext";

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const imageExt = (name) => /\.(jpe?g|png|gif|webp|avif|svg)$/i.test(String(name || ""));

const GalleryManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const path = (searchParams.get("path") || "").replace(/^\/+|\/+$/g, "");
  const [rootUploadName, setRootUploadName] = useState("birthday");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [seoTitle, setSeoTitle] = useState("");
  const [seoAlt, setSeoAlt] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const { showSuccess, showError: toastError } = useMessage();

  const goToPath = (next) => {
    const p = (next || "").replace(/^\/+|\/+$/g, "");
    if (p) setSearchParams({ path: p });
    else setSearchParams({});
  };

  const load = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Not signed in.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const q = path ? `?path=${encodeURIComponent(path)}` : "";
      const res = await fetch(`${apiUrl}/gallery/s3/browse${q}`, {
        headers: { "x-auth-token": token },
      });
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body.message || "Failed to load gallery");
      }
      setData(body.data);
    } catch (e) {
      setError(e.message || "Failed to load");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  const parts = path ? path.split("/").filter(Boolean) : [];
  const uploadFolder = path || rootUploadName.trim() || "birthday";

  const uploadFiles = async (fileList) => {
    const files = fileList ? Array.from(fileList) : [];
    if (!files.length) return;
    const token = localStorage.getItem("token");
    if (!token) {
      toastError("Sign in to upload.");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const preBodyData = {
          folder: uploadFolder,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        };
        if (seoTitle.trim()) preBodyData.title = seoTitle.trim();
        if (seoAlt.trim()) preBodyData.alt = seoAlt.trim();
        if (seoDescription.trim()) preBodyData.description = seoDescription.trim();
        const pre = await fetch(`${apiUrl}/gallery/upload-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token,
          },
          body: JSON.stringify(preBodyData),
        });
        const preBody = await pre.json();
        if (!pre.ok || !preBody?.success || !preBody?.data?.uploadUrl) {
          throw new Error(preBody?.message || `Could not start upload: ${file.name}`);
        }
        const put = await fetch(preBody.data.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload failed: ${file.name}`);
      }
      showSuccess(`Uploaded ${files.length} file${files.length === 1 ? "" : "s"}.`, 4000);
      await load();
    } catch (e) {
      console.error(e);
      toastError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Site gallery</h1>
          <p className="mt-1 text-sm text-gray-600">
            Browse the <code className="text-xs bg-gray-100 px-1 rounded">gallery/</code> tree on the bucket and add
            images. Same upload flow as inventory image uploads.
          </p>
        </div>
        <Link
          to="/settings"
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
        >
          Back to settings
        </Link>
      </div>

      {/* Breadcrumb */}
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <button
          type="button"
          onClick={() => goToPath("")}
          className="font-medium text-blue-600 hover:underline"
        >
          gallery
        </button>
        {parts.map((seg, i) => {
          const prefix = parts.slice(0, i + 1).join("/");
          const isLast = i === parts.length - 1;
          return (
            <span key={prefix} className="inline-flex items-center gap-1">
              <span className="text-gray-400">/</span>
              {isLast ? (
                <span className="text-gray-800">{seg}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => goToPath(prefix)}
                  className="font-medium text-blue-600 hover:underline"
                >
                  {seg}
                </button>
              )}
            </span>
          );
        })}
      </nav>

      <section className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Upload</h2>
        <p className="text-sm text-gray-600 mb-4">
          Files are stored under <code className="text-xs bg-gray-100 px-1 rounded">gallery/{`{folder}`}/</code> as with
          inventory. At the root, set the target folder name; inside a folder, uploads go to the current path.
        </p>
        {!path && (
          <div className="mb-4">
            <label htmlFor="root-folder" className="block text-sm font-medium text-gray-700 mb-1">
              Folder for uploads (at root)
            </label>
            <input
              id="root-folder"
              type="text"
              value={rootUploadName}
              onChange={(e) => setRootUploadName(e.target.value)}
              placeholder="e.g. birthday, wedding, general"
              className="max-w-md w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">Or open a folder below and upload from there without typing.</p>
          </div>
        )}
        {path && (
          <p className="mb-3 text-sm text-gray-800">
            Upload target: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">gallery/{uploadFolder}/</code>
          </p>
        )}
        <div className="mb-4 rounded-lg border border-dashed border-gray-200 bg-gray-50/80 p-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">Optional SEO (per upload batch)</p>
          <p className="text-xs text-gray-600">
            Applied to every file in the next selection. Shown on the public gallery and in search/screen readers. Leave
            blank to use filename-based defaults.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5" htmlFor="g-seo-title">
              Title
            </label>
            <input
              id="g-seo-title"
              type="text"
              value={seoTitle}
              onChange={(e) => setSeoTitle(e.target.value)}
              maxLength={200}
              placeholder="Short image title"
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5" htmlFor="g-seo-alt">
              Alt text
            </label>
            <input
              id="g-seo-alt"
              type="text"
              value={seoAlt}
              onChange={(e) => setSeoAlt(e.target.value)}
              maxLength={200}
              placeholder="Describe the image for accessibility"
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5" htmlFor="g-seo-desc">
              Description
            </label>
            <textarea
              id="g-seo-desc"
              value={seoDescription}
              onChange={(e) => setSeoDescription(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Longer description or caption (also used in gallery text)"
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-900"
            />
          </div>
        </div>
        <div>
          <input
            type="file"
            accept="image/*"
            multiple
            disabled={uploading}
            onChange={(e) => {
              uploadFiles(e.target.files);
              e.target.value = "";
            }}
            className="block text-sm text-gray-600"
          />
          {uploading && <p className="mt-2 text-sm text-gray-500">Uploading…</p>}
        </div>
      </section>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          <section className="mb-8">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Folders</h2>
            {(!data.folders || data.folders.length === 0) && (
              <p className="text-sm text-gray-500">No subfolders here. (Empty S3 &quot;folders&quot; do not appear until an object exists under them.)</p>
            )}
            {data.folders && data.folders.length > 0 && (
              <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {data.folders.map((name) => {
                  const child = path ? `${path}/${name}` : name;
                  return (
                    <li key={child}>
                      <button
                        type="button"
                        onClick={() => goToPath(child)}
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-left text-sm font-medium text-gray-900 hover:border-blue-300 hover:bg-blue-50/50"
                      >
                        <span className="block text-lg mb-0.5" aria-hidden>
                          📁
                        </span>
                        {name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-3">Images in this path</h2>
            {(!data.files || data.files.length === 0) && (
              <p className="text-sm text-gray-500">No files in this path.</p>
            )}
            {data.files && data.files.length > 0 && (
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {data.files.map((f) => {
                  const isImg = imageExt(f.key);
                  const se = f.seo;
                  const hasSeo = se && (se.title || se.alt || se.description);
                  const altText = (se && se.alt) || f.key.split("/").pop() || "";
                  return (
                    <li
                      key={f.key}
                      className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm"
                    >
                      {isImg ? (
                        <a href={f.url} target="_blank" rel="noopener noreferrer" className="block">
                          <img
                            src={f.url}
                            alt={altText}
                            className="h-40 w-full object-cover bg-gray-100"
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <div className="h-40 flex items-center justify-center bg-gray-100 text-xs text-gray-500 px-2 text-center break-all">
                          {f.key.split("/").pop()}
                        </div>
                      )}
                      {hasSeo && (
                        <p className="px-2 pt-1.5 text-[10px] font-semibold text-emerald-800 bg-emerald-50/80">
                          SEO
                        </p>
                      )}
                      <div className="p-2 text-xs text-gray-600 truncate" title={f.key}>
                        {(se && se.title) || f.key.split("/").pop()}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default GalleryManagement;
